import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Typography, Button, Stack, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  Chip, Alert, CircularProgress, Dialog, DialogTitle, DialogContent, DialogActions, IconButton,
  Tooltip, Checkbox, FormControl, InputLabel, Select, MenuItem, TablePagination
} from '@mui/material';
import { CopyOutlined, ReloadOutlined, WhatsAppOutlined } from '@ant-design/icons';
import axios from 'api/axiosInstance';
import endpoints from 'api/endpoints';
import { stickyActionHead, stickyActionCell } from 'utils/stickyColumn';

const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const waDigits = (phone) => {
  let d = String(phone || '').replace(/\D/g, '');
  if (d.length === 10) d = `91${d}`; // assume India when no country code
  return d;
};
const leadMessage = (lead) => {
  const origin = window.location.origin;
  const websiteUrl = lead.slug ? `${origin}/appointment/${lead.slug}` : '';
  const loginUrl = `${origin}/login`;
  const credLines = lead.password_set_by_user
    ? `Admin login: ${loginUrl}\nUsername: ${lead.username}\nPassword: the one you chose when you signed up\n\n`
    : `Admin login: ${loginUrl}\nUsername: ${lead.username}\nPassword: ${lead.password}\n\n`;
  return `Hi ${lead.admin_name || 'there'},\n\n🎉 We've created a website for ${lead.company_name}!\n\n`
    + (websiteUrl ? `Your website: ${websiteUrl}\n` : '')
    + credLines
    + `Please log in and update your password.`;
};

export default function ProspectsList() {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [fCategory, setFCategory] = useState(null); // null = all; '' = uncategorized
  const [fIndustry, setFIndustry] = useState('');
  const [fPlace, setFPlace] = useState('');
  const [selected, setSelected] = useState(() => new Set());
  const [converting, setConverting] = useState(false);
  const [results, setResults] = useState(null); // { created:[], errors:[] }
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const load = useCallback(async () => {
    setLoading(true); setError(''); setSelected(new Set());
    try {
      const resp = await axios.get(endpoints.prospects.list, { headers: authHeaders(), timeout: 30000 });
      setRows(resp.data?.rows || []);
      setCategories(resp.data?.categories || []);
    } catch (err) {
      setError(err.code === 'ECONNABORTED' ? 'Request timed out — please retry.' : (err.response?.data?.detail || 'Could not load prospects'));
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const industries = useMemo(() => [...new Set(rows.map((r) => r.industry).filter(Boolean))].sort(), [rows]);
  const places = useMemo(() => [...new Set(rows.map((r) => r.place).filter(Boolean))].sort(), [rows]);

  const filtered = useMemo(() => rows.filter((r) =>
    (fCategory === null || (r.category || '') === fCategory)
    && (!fIndustry || r.industry === fIndustry)
    && (!fPlace || r.place === fPlace)
  ), [rows, fCategory, fIndustry, fPlace]);

  // Render only the current page — rendering ~1000 MUI rows at once froze the UI.
  useEffect(() => { setPage(0); }, [fCategory, fIndustry, fPlace]);
  const paged = useMemo(() => filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filtered, page, rowsPerPage]);

  const selectableIds = useMemo(() => filtered.filter((r) => r.status !== 'CONVERTED').map((r) => r.id), [filtered]);
  const allSelected = selectableIds.length > 0 && selectableIds.every((id) => selected.has(id));

  const toggle = (id) => setSelected((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(selectableIds));

  const flagAsLeads = async (ids) => {
    if (!ids.length) return;
    setConverting(true); setError('');
    try {
      const resp = await axios.post(endpoints.prospects.convertBulk, { ids }, { headers: authHeaders() });
      setResults(resp.data);
      load();
    } catch (err) {
      setError(err.response?.data?.detail || 'Conversion failed');
    } finally { setConverting(false); }
  };

  // Open the converted company as its admin (impersonate) — same as Company List.
  const handleViewAs = async (companyId) => {
    if (!companyId) return;
    try {
      const superAdminData = {
        token: localStorage.getItem('token'), refreshToken: localStorage.getItem('refreshToken'),
        role: localStorage.getItem('role'), tenant_id: localStorage.getItem('tenant_id'), userData: localStorage.getItem('userData'),
      };
      localStorage.setItem('superAdminData', JSON.stringify(superAdminData));
      const resp = await axios.post(endpoints.company.tokenByUser, { company_id: companyId }, { headers: authHeaders() });
      const data = resp.data;
      localStorage.setItem('refreshToken', data.refresh);
      localStorage.setItem('role', data.role);
      localStorage.setItem('tenant_id', data.company_id);
      localStorage.setItem('token', data.access);
      localStorage.setItem('viewingAsAdmin', 'true');
      localStorage.setItem('userData', JSON.stringify({ id: data.user_id, email: data.username, company: data.company, industry: data.industry, industry_code: data.industry_code }));
      navigate('/dashboard');
      window.location.reload();
    } catch {
      setError('Could not open the company');
    }
  };

  const copy = (t) => navigator.clipboard?.writeText(t);
  const copyAll = () => copy((results?.created || []).map(leadMessage).join('\n\n———\n\n'));
  const sendWhatsApp = (lead) => {
    const d = waDigits(lead.phone);
    const url = `https://wa.me/${d}?text=${encodeURIComponent(leadMessage(lead))}`;
    window.open(url, '_blank');
  };

  return (
    <Box sx={{ maxWidth: 1600, mx: 'auto', py: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>Prospects</Typography>
        <Stack direction="row" spacing={1}>
          <Button variant="contained" onClick={() => navigate('/import-prospects')}>Import prospects</Button>
          <Tooltip title="Refresh"><IconButton onClick={load}><ReloadOutlined /></IconButton></Tooltip>
        </Stack>
      </Stack>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

      {/* Category chips (counts) */}
      <Stack direction="row" spacing={1} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
        <Chip label="All" color={fCategory === null ? 'primary' : 'default'} onClick={() => setFCategory(null)} />
        {categories.map((c) => {
          const val = c.category === '(uncategorized)' ? '' : c.category;
          return (
            <Chip key={c.category} label={`${c.category} · ${c.count}`}
              color={fCategory === val ? 'primary' : 'default'} variant={fCategory === val ? 'filled' : 'outlined'}
              onClick={() => setFCategory(val)} />
          );
        })}
      </Stack>

      {/* Filters + bulk action */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Industry</InputLabel>
          <Select label="Industry" value={fIndustry} onChange={(e) => setFIndustry(e.target.value)}>
            <MenuItem value="">All industries</MenuItem>
            {industries.map((i) => <MenuItem key={i} value={i}>{i}</MenuItem>)}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Place</InputLabel>
          <Select label="Place" value={fPlace} onChange={(e) => setFPlace(e.target.value)}>
            <MenuItem value="">All places</MenuItem>
            {places.map((p) => <MenuItem key={p} value={p}>{p}</MenuItem>)}
          </Select>
        </FormControl>
        {(fIndustry || fPlace) && <Button size="small" onClick={() => { setFIndustry(''); setFPlace(''); }}>Clear filters</Button>}
        <Box sx={{ flex: 1 }} />
        {selected.size > 0 && (
          <Button variant="contained" color="success" disabled={converting} onClick={() => flagAsLeads([...selected])}>
            {converting ? <CircularProgress size={20} color="inherit" /> : `Flag ${selected.size} as Lead${selected.size === 1 ? '' : 's'}`}
          </Button>
        )}
      </Stack>

      <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <Box sx={{ overflowX: 'auto' }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell padding="checkbox">
                      <Checkbox size="small" checked={allSelected}
                        indeterminate={!allSelected && selectableIds.some((id) => selected.has(id))}
                        onChange={toggleAll} disabled={selectableIds.length === 0} />
                    </TableCell>
                    <TableCell>Company</TableCell><TableCell>Admin</TableCell><TableCell>Email</TableCell>
                    <TableCell>Phone</TableCell><TableCell>Place</TableCell><TableCell>Industry</TableCell>
                    <TableCell>Category</TableCell><TableCell>Status</TableCell>
                    <TableCell align="right" sx={stickyActionHead()}>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={10} align="center" sx={{ py: 4, color: 'text.secondary' }}>No prospects</TableCell></TableRow>
                  ) : paged.map((p) => {
                    const converted = p.status === 'CONVERTED';
                    return (
                      <TableRow key={p.id} hover selected={selected.has(p.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox size="small" checked={selected.has(p.id)} disabled={converted} onChange={() => toggle(p.id)} />
                        </TableCell>
                        <TableCell>
                          <Stack direction="row" spacing={1} alignItems="center">
                            {p.branding_logo ? (
                              <Box component="img" src={p.branding_logo} alt=""
                                sx={{ width: 28, height: 28, borderRadius: '6px', objectFit: 'cover', flexShrink: 0, border: '1px solid #E2E8F0', bgcolor: '#fff' }} />
                            ) : (
                              <Box sx={{ width: 28, height: 28, borderRadius: '6px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '0.72rem', fontWeight: 700, color: '#64748B', bgcolor: p.branding_colour || '#F1F5F9' }}>
                                {(p.company_name || '?').charAt(0).toUpperCase()}
                              </Box>
                            )}
                            <span>{p.company_name}</span>
                            {p.self_signup && <Chip size="small" color="warning" variant="outlined" label="Self Signup" sx={{ height: 20, fontSize: '0.68rem' }} />}
                          </Stack>
                        </TableCell>
                        <TableCell>{p.admin_name || '—'}</TableCell>
                        <TableCell>{p.email || '—'}</TableCell>
                        <TableCell>{p.phone || '—'}</TableCell>
                        <TableCell>{p.place || '—'}</TableCell>
                        <TableCell>{p.industry || '—'}</TableCell>
                        <TableCell>{p.category ? <Chip size="small" label={p.category} /> : '—'}</TableCell>
                        <TableCell>{converted
                          ? <Chip size="small" color="success" label="Lead / Company" />
                          : <Chip size="small" variant="outlined" label="New" />}</TableCell>
                        <TableCell align="right" sx={stickyActionCell}>
                          {converted
                            ? <Button size="small" variant="outlined" color="success" onClick={() => handleViewAs(p.converted_company)}>View as company</Button>
                            : <Button size="small" variant="outlined" onClick={() => flagAsLeads([p.id])} disabled={converting}>Flag as Lead</Button>}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <TablePagination
              component="div"
              count={filtered.length}
              page={page}
              onPageChange={(_, p) => setPage(p)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[25, 50, 100]}
              sx={{ borderTop: '1px solid', borderTopColor: 'divider' }}
            />
          </>
        )}
      </Paper>

      {/* Results: created leads with copy / WhatsApp */}
      <Dialog open={!!results} onClose={() => setResults(null)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Leads created</DialogTitle>
        <DialogContent>
          <Alert severity={results?.summary?.created ? 'success' : 'warning'} sx={{ mb: 2 }}>
            Created <strong>{results?.summary?.created || 0}</strong> compan{results?.summary?.created === 1 ? 'y' : 'ies'}
            {results?.summary?.failed ? `, ${results.summary.failed} failed` : ''}. Credentials shown once — send them now.
          </Alert>
          {results?.created?.length > 0 && (
            <Stack spacing={1.5}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Button size="small" startIcon={<CopyOutlined />} onClick={copyAll}>Copy all messages</Button>
              </Box>
              {results.created.map((lead) => (
                <Paper key={lead.company_id} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                  <Typography variant="body2" fontWeight={700}>{lead.company_name}</Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                    {lead.username} / <code>{lead.password}</code>{lead.phone ? ` · 📞 ${lead.phone}` : ''}
                  </Typography>
                  <Stack direction="row" spacing={1} sx={{ mt: 1 }}>
                    <Button size="small" startIcon={<CopyOutlined />} onClick={() => copy(leadMessage(lead))}>Copy message</Button>
                    <Button size="small" color="success" startIcon={<WhatsAppOutlined />} onClick={() => sendWhatsApp(lead)}>WhatsApp</Button>
                  </Stack>
                </Paper>
              ))}
            </Stack>
          )}
          {results?.errors?.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" color="error" sx={{ mb: 0.5 }}>Skipped ({results.errors.length})</Typography>
              {results.errors.map((e, i) => (
                <Typography key={i} variant="caption" color="text.secondary" sx={{ display: 'block' }}>{e.company_name}: {e.error}</Typography>
              ))}
            </Box>
          )}
        </DialogContent>
        <DialogActions><Button variant="contained" onClick={() => setResults(null)}>Done</Button></DialogActions>
      </Dialog>
    </Box>
  );
}
